/**
 * Super Admin Tier Edit Page - 요금제 추가/수정 페이지
 */

import { Trans, t } from '@lingui/macro';
import { ChevronLeft, Loader2, Save } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tier, TIERS_COL } from '../../models/Tier';
import { Fire } from '../../services/FireClient';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

interface TierFormData {
    id: string;
    name: string;
    description: string;
    monthlyPrice: number;
    yearlyPrice: number;
    currency: 'JPY' | 'KRW' | 'USD';
    storageGB: number;
    monthlyDownloadGB: number;
    memberCount: number;
    channelCount: number;
    botCount: number;
    customBranding: boolean;
    apiAccess: boolean;
    prioritySupport: boolean;
    analytics: boolean;
    isActive: boolean;
    displayOrder: number;
}

const defaultFormData: TierFormData = {
    id: '',
    name: '',
    description: '',
    monthlyPrice: 0,
    yearlyPrice: 0,
    currency: 'JPY',
    storageGB: 1,
    monthlyDownloadGB: 5,
    memberCount: 5,
    channelCount: 3,
    botCount: 1,
    customBranding: false,
    apiAccess: false,
    prioritySupport: false,
    analytics: false,
    isActive: true,
    displayOrder: 0,
};

export const SuperAdminTierEditPage: React.FC = () => {
    const navigate = useNavigate();
    const { tierId } = useParams<{ tierId: string }>();
    const isNew = tierId === 'new';

    const [formData, setFormData] = useState<TierFormData>(defaultFormData);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isNew && tierId) {
            loadTier(tierId);
        }
    }, [tierId, isNew]);

    const loadTier = async (id: string) => {
        try {
            setLoading(true);
            const docRef = doc(Fire.store, TIERS_COL, id);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const tier = docSnap.data() as Tier;
                setFormData({
                    id: tier.id,
                    name: tier.name,
                    description: tier.description || '',
                    monthlyPrice: tier.price.monthly,
                    yearlyPrice: tier.price.yearly,
                    currency: tier.price.currency,
                    storageGB: tier.limits.storageBytes / (1024 * 1024 * 1024),
                    monthlyDownloadGB: tier.limits.monthlyDownloadBytes / (1024 * 1024 * 1024),
                    memberCount: tier.limits.memberCount,
                    channelCount: tier.limits.channelCount,
                    botCount: tier.limits.botCount,
                    customBranding: tier.features.customBranding,
                    apiAccess: tier.features.apiAccess,
                    prioritySupport: tier.features.prioritySupport,
                    analytics: tier.features.analytics,
                    isActive: tier.isActive,
                    displayOrder: tier.displayOrder,
                });
            }
        } catch (error) {
            console.error('Failed to load tier:', error);
            alert('요금제를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.id.trim()) {
            alert('요금제 ID를 입력해주세요.');
            return;
        }

        if (!formData.name.trim()) {
            alert('요금제 이름을 입력해주세요.');
            return;
        }

        try {
            setSaving(true);

            const tierData: any = {
                id: formData.id.toUpperCase(),
                name: formData.name,
                description: formData.description,
                price: {
                    monthly: formData.monthlyPrice,
                    yearly: formData.yearlyPrice,
                    currency: formData.currency,
                },
                limits: {
                    storageBytes: formData.storageGB * 1024 * 1024 * 1024,
                    monthlyDownloadBytes: formData.monthlyDownloadGB * 1024 * 1024 * 1024,
                    memberCount: formData.memberCount,
                    channelCount: formData.channelCount,
                    botCount: formData.botCount,
                },
                features: {
                    customBranding: formData.customBranding,
                    apiAccess: formData.apiAccess,
                    prioritySupport: formData.prioritySupport,
                    analytics: formData.analytics,
                },
                isActive: formData.isActive,
                displayOrder: formData.displayOrder,
                updatedAt: serverTimestamp(),
            };

            if (isNew) {
                tierData.createdAt = serverTimestamp();
            }

            await setDoc(doc(Fire.store, TIERS_COL, formData.id.toUpperCase()), tierData, { merge: true });
            navigate('/super-admin/tiers');
        } catch (error) {
            console.error('Failed to save tier:', error);
            alert('요금제 저장에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const updateField = <K extends keyof TierFormData>(key: K, value: TierFormData[K]) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/super-admin/tiers')}>
                    <ChevronLeft className="w-5 h-5" />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold">
                        {isNew ? '새 요금제' : `${formData.name} 수정`}
                    </h1>
                    <p className="text-gray-500">요금제 정보를 입력합니다.</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* 기본 정보 */}
                <Card>
                    <CardHeader>
                        <CardTitle>기본 정보</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="id">요금제 ID *</Label>
                                <Input
                                    id="id"
                                    value={formData.id}
                                    onChange={(e) => updateField('id', e.target.value.toUpperCase())}
                                    placeholder="FREE, BASIC, PREMIUM"
                                    disabled={!isNew}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="name">표시 이름 *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => updateField('name', e.target.value)}
                                    placeholder="무료, 베이직, 프리미엄"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="description">설명</Label>
                            <Input
                                id="description"
                                value={formData.description}
                                onChange={(e) => updateField('description', e.target.value)}
                                placeholder="요금제 설명"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="displayOrder">표시 순서</Label>
                                <Input
                                    id="displayOrder"
                                    type="number"
                                    value={formData.displayOrder}
                                    onChange={(e) => updateField('displayOrder', parseInt(e.target.value) || 0)}
                                />
                            </div>
                            <div className="space-y-2 flex items-end">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.isActive}
                                        onChange={(e) => updateField('isActive', e.target.checked)}
                                        className="w-4 h-4"
                                    />
                                    <span>활성화</span>
                                </label>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* 가격 정보 */}
                <Card>
                    <CardHeader>
                        <CardTitle>가격 정보</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="monthlyPrice">월간 가격</Label>
                                <Input
                                    id="monthlyPrice"
                                    type="number"
                                    value={formData.monthlyPrice}
                                    onChange={(e) => updateField('monthlyPrice', parseInt(e.target.value) || 0)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="yearlyPrice">연간 가격</Label>
                                <Input
                                    id="yearlyPrice"
                                    type="number"
                                    value={formData.yearlyPrice}
                                    onChange={(e) => updateField('yearlyPrice', parseInt(e.target.value) || 0)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="currency">통화</Label>
                                <select
                                    id="currency"
                                    value={formData.currency}
                                    onChange={(e) => updateField('currency', e.target.value as 'JPY' | 'KRW' | 'USD')}
                                    className="w-full h-10 px-3 border rounded-md"
                                >
                                    <option value="JPY">JPY (¥)</option>
                                    <option value="KRW">KRW (₩)</option>
                                    <option value="USD">USD ($)</option>
                                </select>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* 제한 사항 */}
                <Card>
                    <CardHeader>
                        <CardTitle>제한 사항</CardTitle>
                        <CardDescription>0 = 무제한</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="storageGB">저장소 (GB)</Label>
                                <Input
                                    id="storageGB"
                                    type="number"
                                    value={formData.storageGB}
                                    onChange={(e) => updateField('storageGB', parseFloat(e.target.value) || 0)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="monthlyDownloadGB">월간 다운로드 (GB)</Label>
                                <Input
                                    id="monthlyDownloadGB"
                                    type="number"
                                    value={formData.monthlyDownloadGB}
                                    onChange={(e) => updateField('monthlyDownloadGB', parseFloat(e.target.value) || 0)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="memberCount">최대 멤버 수</Label>
                                <Input
                                    id="memberCount"
                                    type="number"
                                    value={formData.memberCount}
                                    onChange={(e) => updateField('memberCount', parseInt(e.target.value) || 0)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="channelCount">최대 채널 수</Label>
                                <Input
                                    id="channelCount"
                                    type="number"
                                    value={formData.channelCount}
                                    onChange={(e) => updateField('channelCount', parseInt(e.target.value) || 0)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="botCount">최대 봇 수</Label>
                                <Input
                                    id="botCount"
                                    type="number"
                                    value={formData.botCount}
                                    onChange={(e) => updateField('botCount', parseInt(e.target.value) || 0)}
                                />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* 기능 */}
                <Card>
                    <CardHeader>
                        <CardTitle>기능</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.apiAccess}
                                    onChange={(e) => updateField('apiAccess', e.target.checked)}
                                    className="w-4 h-4"
                                />
                                <span>API 접근</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.analytics}
                                    onChange={(e) => updateField('analytics', e.target.checked)}
                                    className="w-4 h-4"
                                />
                                <span>분석 기능</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.customBranding}
                                    onChange={(e) => updateField('customBranding', e.target.checked)}
                                    className="w-4 h-4"
                                />
                                <span>커스텀 브랜딩</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.prioritySupport}
                                    onChange={(e) => updateField('prioritySupport', e.target.checked)}
                                    className="w-4 h-4"
                                />
                                <span>우선 지원</span>
                            </label>
                        </div>
                    </CardContent>
                </Card>

                {/* 저장 버튼 */}
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => navigate('/super-admin/tiers')}>
                        취소
                    </Button>
                    <Button type="submit" disabled={saving}>
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        <Save className="w-4 h-4 mr-2" />
                        저장
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default SuperAdminTierEditPage;
